<template>
	<div class="container-fluid p-4">
		<div
			class="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-4"
		>
			<div>
				<h1 class="mb-0">Inventory</h1>
				<p class="text-muted mb-0">
					Stock on hand, by product and storage location, in each product's base
					unit. Every change to it is a movement in the ledger.
				</p>
			</div>
			<RouterLink class="btn btn-primary" to="/_/inventory/receive">
				<i class="bi bi-box-arrow-in-down me-1"></i>Receive product
			</RouterLink>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">
			{{ error }}
		</div>

		<div class="card">
			<div class="card-body">
				<div class="row g-2 align-items-end mb-3">
					<div class="col-12 col-md-4 col-lg-3">
						<label class="form-label" for="stockLocationFilter">
							Storage location
						</label>
						<select
							id="stockLocationFilter"
							v-model="locationFilter"
							class="form-select"
						>
							<option :value="null">All locations</option>
							<option
								v-for="location in locationOptions"
								:key="location.id"
								:value="location.id"
							>
								{{ location.path }}
							</option>
						</select>
					</div>
					<div class="col-12 col-md-4 col-lg-3">
						<label class="form-label" for="stockProductFilter">Product</label>
						<input
							id="stockProductFilter"
							v-model="productFilter"
							type="search"
							class="form-control"
							placeholder="Filter by product name"
						/>
					</div>
					<div class="col-12 col-lg text-muted small pb-2">
						{{ filteredStock.length }} of {{ stock.length }} stock rows shown
					</div>
				</div>

				<div v-if="loading" class="text-center py-4">
					<div class="spinner-border text-primary" role="status">
						<span class="visually-hidden">Loading…</span>
					</div>
				</div>

				<div v-else-if="stock.length === 0" class="text-center py-4 text-muted">
					No stock is on hand. Receive a vendor delivery to put product on the
					shelf.
				</div>

				<div
					v-else-if="filteredStock.length === 0"
					class="text-center py-4 text-muted"
				>
					No stock matches the location and product filters.
				</div>

				<div v-else class="table-responsive">
					<table class="table table-striped table-hover align-middle">
						<thead class="table-light">
							<tr>
								<th scope="col">Product</th>
								<th scope="col">Storage location</th>
								<th scope="col">
									Amount
									<div class="text-muted small fw-normal">
										In the product's base unit
									</div>
								</th>
								<th scope="col">
									Method
									<div class="text-muted small fw-normal">
										How the amount is known
									</div>
								</th>
								<th scope="col">Updated</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="row in filteredStock" :key="row.id">
								<td class="fw-semibold">{{ row.product_name }}</td>
								<td>{{ row.location_path || row.location_name }}</td>
								<td>
									<LocalizedNumber :value="row.amount" />
									<span v-if="row.unit" class="ms-1 text-muted">
										{{ row.unit }}
									</span>
									<span
										v-else
										class="ms-1 text-muted small"
										title="Set this product's base unit on the Units page."
									>
										(no unit set)
									</span>
								</td>
								<td>
									<span class="badge text-bg-light border">
										{{ methodLabel(row.method) }}
									</span>
								</td>
								<td>
									<TimeRelativeWithTooltip :time="row.updated" />
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import { apiClient } from "@/client";
import LocalizedNumber from "@/components/LocalizedNumber.vue";
import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import type { InventoryStock, InventoryStockMethod } from "@/type/api";

// The method an amount is known by, read on the stock itself (see
// doc/design/mvp/inventory.md, "Stock"): a dip-stick reading is approximate and
// is a natural subject of a frequent count.
const METHOD_LABELS: Record<InventoryStockMethod, string> = {
	counted: "Counted",
	weighed: "Weighed",
	estimated: "Estimated",
};

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

const stock = ref<InventoryStock[]>([]);
const loading = ref(false);
const error = ref("");

// An error is both the notification and the page state: the toast tells the
// operator, and the inline strip keeps the reason on screen after it dismisses.
function fail(title: string, e: any, fallback: string) {
	const message = e?.message || fallback;
	error.value = message;
	showNotification(title, message);
}

const locationFilter = ref<number | null>(null);
const productFilter = ref("");

// Only locations that hold stock are offered: a location with none would filter
// the table to nothing, which the empty state says more clearly.
const locationOptions = computed(() => {
	const byID = new Map<number, string>();
	for (const row of stock.value) {
		byID.set(row.storage_location_id, row.location_path || row.location_name);
	}
	return [...byID.entries()]
		.map(([id, path]) => ({ id, path }))
		.sort((a, b) => a.path.localeCompare(b.path));
});

const filteredStock = computed(() => {
	const needle = productFilter.value.trim().toLowerCase();
	const rows = stock.value.filter((row) => {
		if (
			locationFilter.value !== null &&
			row.storage_location_id !== locationFilter.value
		) {
			return false;
		}
		if (needle !== "" && !row.product_name.toLowerCase().includes(needle)) {
			return false;
		}
		return true;
	});
	// Products and locations read together: one product's holdings sit adjacent
	// however the server ordered the rows.
	return rows.sort(
		(a, b) =>
			a.product_name.localeCompare(b.product_name) ||
			(a.location_path || a.location_name).localeCompare(
				b.location_path || b.location_name,
			),
	);
});

function methodLabel(method: InventoryStockMethod): string {
	return METHOD_LABELS[method] ?? method;
}

async function loadStock() {
	loading.value = true;
	error.value = "";
	try {
		// The district's whole stock ledger through the generic QUERY endpoint
		// (the legacy GET list's replacement, doc/in-progress/query.md §11 Phase
		// 4). The walk holds every row, so the location and product controls
		// below keep narrowing the collection in memory exactly as before.
		const page = await apiClient.JSONQueryAll<InventoryStock>(
			"/api/inventory/stock",
		);
		stock.value = page.items;
	} catch (e: any) {
		fail("Failed to load stock", e, "Please reload the page.");
	} finally {
		loading.value = false;
	}
}

onMounted(loadStock);
</script>

<style scoped>
.table-responsive {
	overflow-x: auto;
}

th {
	white-space: nowrap;
}
</style>
